<template>
  <div class="tinymce-editor-container">
    <Loading v-if="loading" />
    <Editor
      :id="editorId"
      :apiKey="apiKey"
      v-model="content"
      :init="editorConfig"
      :disabled="disabled"
      @onInit="handleInit"
    />
    <div v-if="showWordCount" class="word-counter">{{ wordCount }} words</div>
  </div>
</template>

<script setup>
import { ref, watch, computed, getCurrentInstance, onMounted } from 'vue';
import { default as Editor } from '@tinymce/tinymce-vue';
import { ElLoading } from 'element-plus';
import { useNotification } from '@/components/ui/notification';
import Loading from '@/components/ui/Loading.vue';

const app = getCurrentInstance();
const ajax = app.appContext.config.globalProperties.$ajax;
const { openNotBottom } = useNotification();

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: 'Start typing...',
  },
  height: {
    type: [Number, String],
    default: 500,
  },
  showWordCount: {
    type: Boolean,
    default: true,
  },
  uploadUrl: {
    type: String,
    default: 'announcement/upload-announcement-media',
  },
  imageSizeLimit: {
    type: Number,
    default: 10, // MB
  },
  videoSizeLimit: {
    type: Number,
    default: 50, // MB
  },
  editorId: {
    type: String,
    default: () => `tinymce-editor-${Math.random().toString(36).substr(2, 9)}`,
  },
});

const emit = defineEmits(['update:modelValue', 'editorInit', 'contentChange']);

const content = ref(props.modelValue || '');
const apiKey = ref('c1alfret2hep8ly0ljv4bzkt8u257yn7p2w87xmdes22tsz4');
const loading = ref(false);
const editorInstance = ref(null);
const wordCount = ref(0);

// Full featured TinyMCE configuration based on the demo
const editorConfig = computed(() => ({
  apiKey: apiKey.value,
  height: props.height,

  // Menu configuration
  menu: {
    file: { title: 'File', items: 'newdocument restoredraft | export | deleteallconversations' },
    edit: { title: 'Edit', items: 'undo redo | cut copy paste pastetext | selectall | searchreplace' },
    view: { title: 'View', items: 'code | visualaid visualchars visualblocks | spellchecker | fullscreen' },
    insert: {
      title: 'Insert',
      items:
        'image link accordion codesample inserttable | charmap emoticons hr | pagebreak nonbreaking anchor tableofcontents | insertdatetime',
    },
    format: {
      title: 'Format',
      items:
        'bold italic underline strikethrough superscript subscript codeformat | styles blocks fontfamily fontsize align lineheight | forecolor backcolor | language | removeformat',
    },
    tools: { title: 'Tools', items: 'spellchecker spellcheckerlanguage | a11ycheck code wordcount' },
    table: { title: 'Table', items: 'inserttable | cell row column | advtablesort | tableprops deletetable' },
  },
  menubar: 'file edit view insert format tools table',

  // Comprehensive plugin list from the full-featured demo
  plugins: [
    'importcss',
    'searchreplace',
    'autolink',
    'autosave',
    'directionality',
    'code',
    'visualblocks',
    'visualchars',
    'fullscreen',
    'image',
    'link',
    'codesample',
    'table',
    'charmap',
    'pagebreak',
    'nonbreaking',
    // 'anchor',
    'insertdatetime',
    'advlist',
    'lists',
    'wordcount',
    'charmap',
    'quickbars',
    'emoticons',
    // 'accordion'
  ],

  // Comprehensive toolbar configuration matching the demo
  toolbar:
    'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | align numlist bullist | link image | table accordion | lineheight outdent indent | forecolor backcolor removeformat | charmap emoticons | code fullscreen | pagebreak anchor codesample | ltr rtl',
  toolbar_mode: 'sliding',

  // Font options
  font_family_formats:
    'Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats; Franklin Gothic Book=franklin gothic book,sans-serif; Franklin Gothic Medium=franklin gothic medium,sans-serif; Franklin Gothic Demi=franklin gothic demi,sans-serif',

  font_size_formats: '8pt 9pt 10pt 11pt 12pt 14pt 16pt 18pt 20pt 24pt 28pt 32pt 36pt 48pt 72pt',

  // Block formats
  block_formats:
    'Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Heading 5=h5; Heading 6=h6; Preformatted=pre',

  // Style formats
  style_formats: [
    {
      title: 'Headings',
      items: [
        { title: 'Heading 1', format: 'h1' },
        { title: 'Heading 2', format: 'h2' },
        { title: 'Heading 3', format: 'h3' },
        { title: 'Heading 4', format: 'h4' },
        { title: 'Heading 5', format: 'h5' },
        { title: 'Heading 6', format: 'h6' },
      ],
    },
    {
      title: 'Inline',
      items: [
        { title: 'Bold', format: 'bold' },
        { title: 'Italic', format: 'italic' },
        { title: 'Underline', format: 'underline' },
        { title: 'Strikethrough', format: 'strikethrough' },
        { title: 'Superscript', format: 'superscript' },
        { title: 'Subscript', format: 'subscript' },
        { title: 'Code', format: 'code' },
      ],
    },
    {
      title: 'Blocks',
      items: [
        { title: 'Paragraph', format: 'p' },
        { title: 'Blockquote', format: 'blockquote' },
        { title: 'Div', format: 'div' },
        { title: 'Pre', format: 'pre' },
      ],
    },
    {
      title: 'Alignment',
      items: [
        { title: 'Left', format: 'alignleft' },
        { title: 'Center', format: 'aligncenter' },
        { title: 'Right', format: 'alignright' },
        { title: 'Justify', format: 'alignjustify' },
      ],
    },
  ],

  // Template configurations
  templates: [
    {
      title: 'New Table',
      description: 'creates a new table',
      content:
        '<div class="mceTmpl"><table width="98%"  border="0" cellspacing="0" cellpadding="0"><tr><th scope="col"> </th><th scope="col"> </th></tr><tr><td> </td><td> </td></tr></table></div>',
    },
    {
      title: 'Starting my story',
      description: 'A cure for writers block',
      content: 'Once upon a time...',
    },
    {
      title: 'New list with dates',
      description: 'New List with dates',
      content:
        '<div class="mceTmpl"><span class="cdate">cdate</span><br /><span class="mdate">mdate</span><h2>My List</h2><ul><li></li><li></li></ul></div>',
    },
  ],

  // Image settings
  image_advtab: true,
  image_caption: true,
  image_title: true,
  image_dimensions: true,
  automatic_uploads: false,

  // Media settings
  media_live_embeds: true,
  media_dimensions: true,
  media_alt_source: true,
  media_poster: true,

  // Link settings
  link_assume_external_targets: true,
  link_target_list: [
    { title: 'None', value: '' },
    { title: 'Same page', value: '_self' },
    { title: 'New page', value: '_blank' },
    { title: 'Parent frame', value: '_parent' },
  ],

  // Table settings
  table_appearance_options: true,
  table_advtab: true,
  table_cell_advtab: true,
  table_row_advtab: true,
  table_class_list: [
    { title: 'None', value: '' },
    { title: 'Table with borders', value: 'table-bordered' },
    { title: 'Table striped', value: 'table-striped' },
  ],

  // Other settings
  placeholder: props.placeholder,
  quickbars_selection_toolbar: 'bold italic | quicklink h2 h3 blockquote quickimage quicktable',
  quickbars_insert_toolbar: false,
  contextmenu: 'link image table',
  noneditable_class: 'mceNonEditable',

  // Autosave settings
  autosave_ask_before_unload: true,
  autosave_interval: '30s',
  autosave_prefix: '{path}{query}-{id}-',
  autosave_restore_when_empty: false,
  autosave_retention: '2m',

  // Link list
  link_list: [
    { title: 'My page 1', value: 'https://www.tiny.cloud' },
    { title: 'My page 2', value: 'http://www.moxiecode.com' },
  ],
  importcss_append: true,

  // Status bar
  statusbar: true,
  branding: false,

  // Additional features
  visualblocks_default_state: false,
  end_container_on_empty_block: true,
  resize: true,

  // File picker
  file_picker_types: 'image media',
  file_picker_callback: async (callback, value, meta) => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', meta.filetype === 'image' ? 'image/png,image/jpeg,image/jpg,image/webp,image/svg+xml' : 'video/*');

    input.onchange = async () => {
      const file = input.files[0];
      if (file.type === 'image/gif') {
        openNotBottom('GIF images are not allowed.', 'error');
        return;
      }
      try {
        const result = await uploadMedia(file);
        if (meta.filetype === 'video') {
          callback(result.src, {
            title: file.name,
          });
        } else {
          callback(result.src, {
            title: file.name,
            alt: file.name,
          });
        }
      } catch (error) {
        const msg = typeof error === 'string' ? error : 'Could not upload media.';
        editorInstance.value?.windowManager.alert(msg);
      }
    };
    input.click();
  },

  // Content style with custom fonts - matching demo styles
  content_style: `
    .mymention { color: gray; }
    body { 
      font-family: Helvetica, Arial, sans-serif; 
      font-size: 16px;
      line-height: 1.6;
      color: #333;
      padding: 10px;
    }

    p { margin: 10px 0; }
    h1, h2, h3, h4, h5, h6 { 
      margin: 16px 0 8px 0;
      font-weight: 500;
      line-height: 1.3;
    }
    h1 { font-size: 32px; }
    h2 { font-size: 28px; }
    h3 { font-size: 24px; }
    h4 { font-size: 20px; }
    h5 { font-size: 16px; }
    h6 { font-size: 14px; }

    ul, ol {
      padding-left: 24px;
      margin: 12px 0;
    }

    li { margin-bottom: 6px; }

    table {
      border-collapse: collapse;
      width: 100%;
      margin: 16px 0;
    }

    table td, table th {
      border: 1px solid #ddd;
      padding: 10px;
    }

    table th {
      background-color: #f5f5f5;
      font-weight: 600;
      text-align: left;
    }

    table.table-bordered {
      border: 2px solid #333;
    }

    table.table-striped tbody tr:nth-child(odd) {
      background-color: #f9f9f9;
    }

    blockquote {
      border-left: 4px solid #ccc;
      margin: 16px 0;
      padding: 10px 20px;
      color: #666;
      background-color: #f9f9f9;
    }

    pre {
      background-color: #f4f4f4;
      border: 1px solid #ddd;
      border-radius: 4px;
      padding: 12px;
      overflow-x: auto;
      font-family: 'Courier New', Courier, monospace;
      font-size: 13px;
    }

    code {
      background-color: #f4f4f4;
      padding: 2px 6px;
      border-radius: 3px;
      font-family: 'Courier New', Courier, monospace;
      font-size: 13px;
    }

    a {
      color: #0066cc;
      text-decoration: underline;
    }

    a:hover {
      color: #0052a3;
    }

    img {
      max-width: 100%;
      height: auto;
      display: block;
      margin: 10px 0;
    }

    video {
      max-width: 100%;
      height: auto;
      display: block;
      margin: 10px 0;
    }

    hr {
      border: none;
      border-top: 1px solid #ddd;
      margin: 20px 0;
    }

    .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before {
      color: #999;
      font-style: italic;
    }

    /* Accordion styles */
    details {
      border: 1px solid #ddd;
      border-radius: 4px;
      margin: 10px 0;
      padding: 0;
    }

    details summary {
      background-color: #f5f5f5;
      padding: 12px 16px;
      cursor: pointer;
      font-weight: 600;
      border-radius: 4px;
      list-style: none;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    details summary::-webkit-details-marker {
      display: none;
    }

    details summary::after {
      content: '+';
      font-size: 18px;
      font-weight: bold;
      color: #666;
      transition: transform 0.2s ease;
    }

    details[open] summary::after {
      content: '-';
    }

    details[open] summary {
      border-bottom: 1px solid #ddd;
      border-radius: 4px 4px 0 0;
    }

    details > div {
      padding: 12px 16px;
    }

    .mce-accordion {
      border: 1px solid #ddd;
      border-radius: 4px;
      margin: 10px 0;
    }

    .mce-accordion-summary {
      background-color: #f5f5f5;
      padding: 12px 16px;
      cursor: pointer;
      font-weight: 600;
    }

    .mce-accordion-body {
      padding: 12px 16px;
      border-top: 1px solid #ddd;
    }
  `,

  // Extended valid elements for videos and other media
  extended_valid_elements: 'video[*],source[*],iframe[*]',

  // Setup function for custom behavior
  setup: (editor) => {
    editorInstance.value = editor;

    // Handle drag and drop
    editor.on('drop', async (e) => {
      if (e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        e.preventDefault();

        if (file.type === 'image/gif') {
          openNotBottom('GIF images are not allowed.', 'error');
          return;
        }

        try {
          const result = await uploadMedia(file);
          if (file.type.startsWith('video/')) {
            const insertedContent = `<video controls src="${result.src}"></video>`;
            editor.insertContent(insertedContent);
          } else if (file.type.startsWith('image/')) {
            const insertedContent = `<img src="${result.src}" alt="${file.name}" title="${file.name}" />`;
            editor.insertContent(insertedContent);
          }
        } catch (error) {
          openNotBottom(error, 'error');
        }
      }
    });

    // Update content on change
    const updateContent = () => {
      const newContent = editor.getContent();
      emit('update:modelValue', newContent);
      emit('contentChange', newContent);

      // Update word count
      if (props.showWordCount) {
        const text = editor.getContent({ format: 'text' });
        wordCount.value = text.trim() ? text.trim().split(/\s+/).length : 0;
      }
    };

    editor.on('keyup', updateContent);
    editor.on('change', updateContent);
    editor.on('paste', updateContent);
    editor.on('undo', updateContent);
    editor.on('redo', updateContent);

    // Disable notifications
    editor.on('BeforeOpenNotification', (e) => {
      e.preventDefault();
      return false;
    });

    // Custom button examples
    editor.ui.registry.addButton('customInsertButton', {
      text: 'Insert Custom',
      onAction: () => {
        editor.insertContent('&nbsp;<strong>Custom content!</strong>&nbsp;');
      },
    });
  },
}));

// Upload media function
const uploadMedia = (file) => {
  return new Promise((resolve, reject) => {
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');

    const IMAGE_SIZE_LIMIT = props.imageSizeLimit * 1024 * 1024;
    const VIDEO_SIZE_LIMIT = props.videoSizeLimit * 1024 * 1024;

    if (isImage && file.size > IMAGE_SIZE_LIMIT) {
      reject(`Uploaded images must not exceed ${props.imageSizeLimit}MB.`);
      return;
    }

    if (isVideo && file.size > VIDEO_SIZE_LIMIT) {
      reject(`Uploaded videos must not exceed ${props.videoSizeLimit}MB.`);
      return;
    }

    const upload = async () => {
      let formData = new FormData();
      formData.append('media', file);
      const loadingInstance = ElLoading.service({ fullscreen: true, background: 'rgba(0,0,0,0.45)' });

      try {
        const res = await ajax.post(props.uploadUrl, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        resolve({
          src: res.data.data,
        });
      } catch (error) {
        const msg = error?.response?.data?.message || error?.response?.data?.data?.message || 'Could not upload media.';
        reject(msg);
      } finally {
        loadingInstance.close();
      }
    };

    upload();
  });
};

// Handle editor initialization
const handleInit = (event, editor) => {
  editorInstance.value = editor;
  emit('editorInit', editor);
};

// Watch for external changes to modelValue
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== content.value) {
      content.value = newValue;
    }
  }
);

// Public methods (can be accessed via ref)
defineExpose({
  getContent: (options) => editorInstance.value?.getContent(options) || '',
  setContent: (value) => {
    if (editorInstance.value) {
      editorInstance.value.setContent(value);
    }
  },
  insertContent: (value) => {
    if (editorInstance.value) {
      editorInstance.value.insertContent(value);
    }
  },
  getEditor: () => editorInstance.value,
  focus: () => editorInstance.value?.focus(),
  clear: () => {
    if (editorInstance.value) {
      editorInstance.value.setContent('');
    }
  },
});

onMounted(() => {
  content.value = props.modelValue;
});
</script>

<style lang="scss" scoped>
.tinymce-editor-container {
  width: 100%;
  position: relative;
  font-family: var(--font, 'Franklin Gothic Book', sans-serif);

  .word-counter {
    position: absolute;
    bottom: 12px;
    right: 16px;
    font-size: 12px;
    color: #666;
    background: rgba(255, 255, 255, 0.9);
    padding: 4px 8px;
    border-radius: 4px;
    z-index: 10;
    pointer-events: none;
    display: none;
  }
}
</style>

<style lang="scss">
// Global TinyMCE styling
.tox {
  .tox-statusbar__path{
    display: none !important;
  }
  .tox-statusbar__text-container{
    justify-content: flex-end !important;
  }
  .tox-toolbar,
  .tox-toolbar__primary {
    background-color: #f7f7f7 !important;
  }

  .tox-edit-area {
    border-top: 1px solid #e3e3e3 !important;
  }

  .tox-statusbar {
    border-top: 1px solid #e3e3e3 !important;
  }

  .tox-button {
    font-family: var(--font-med, 'Franklin Gothic Medium', sans-serif);

    &::before {
      display: none;
    }
  }

  .tox-button--secondary {
    border: 1px solid #ddd !important;
    background-color: #fff !important;
    color: #333 !important;

    &:hover {
      border-color: #bbb !important;
      background-color: #f5f5f5 !important;
    }
  }

  .tox-button--primary {
    background-color: var(--PB-100, #0066cc) !important;
    border-color: var(--PB-100, #0066cc) !important;

    &:hover {
      background-color: var(--PB-120, #0052a3) !important;
      border-color: var(--PB-120, #0052a3) !important;
    }
  }
}

// Dialog styling
.tox-dialog {
  font-family: var(--font, 'Franklin Gothic Book', sans-serif);

  .tox-dialog__header {
    padding: 16px 16px 8px;

    .tox-dialog__title {
      font-family: var(--font-demi, 'Franklin Gothic Demi', sans-serif);
      font-size: 18px;
      font-weight: 600;
    }
  }

  .tox-dialog__body-content {
    padding: 16px;
  }

  .tox-form__group {
    margin-bottom: 16px;
  }

  .tox-label {
    font-family: var(--font-med, 'Franklin Gothic Medium', sans-serif);
    font-size: 14px;
    margin-bottom: 6px;
  }

  .tox-textfield,
  .tox-textarea {
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 8px;
    font-family: var(--font, 'Franklin Gothic Book', sans-serif);

    &:focus {
      border-color: var(--dark-blue, #0066cc);
      outline: none;
      box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.1);
    }
  }

  .tox-dialog__footer {
    padding: 12px 16px;
    border-top: 1px solid #e3e3e3;
  }
}

// Menu styling
.tox-menu {
  font-family: var(--font, 'Franklin Gothic Book', sans-serif);
}

// Collection styling
.tox-collection__item {
  font-family: var(--font, 'Franklin Gothic Book', sans-serif);
}

// Toolbar button styling
.tox-tbtn {
  &:hover {
    background-color: #e3e3e3 !important;
  }

  &--enabled,
  &--enabled:hover {
    background-color: #d4d4d4 !important;
  }
}

// Mention styling from demo
span.mymention {
  color: gray !important;
}

// Card styling from demo
.tox div.card {
  width: 240px;
  background: white;
  border: 1px solid #ccc;
  border-radius: 3px;
  box-shadow: 0 4px 8px 0 rgba(34, 47, 62, 0.1);
  padding: 8px;
  font-size: 14px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen-Sans, Ubuntu, Cantarell, 'Helvetica Neue',
    sans-serif;

  &::after {
    content: '';
    clear: both;
    display: table;
  }

  h1 {
    font-size: 14px;
    font-weight: bold;
    margin: 0 0 8px;
    padding: 0;
    line-height: normal;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen-Sans, Ubuntu, Cantarell, 'Helvetica Neue',
      sans-serif;
  }

  p {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen-Sans, Ubuntu, Cantarell, 'Helvetica Neue',
      sans-serif;
  }

  img.avatar {
    width: 48px;
    height: 48px;
    margin-right: 8px;
    float: left;
  }
}
.tox-dialog__body-content .tox-textarea,
.tox-form__group--stretched{
  height: 100% !important;
  min-height: unset !important;
}
</style>
